import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
  baseURL as defaultBaseURL,
  CRATE_USERS_BASE_URL,
  FALLBACK_TO_LOCAL_STORAGE,
  REFRESH_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
  SESSION_TOKEN_COOKIE_KEY,
  SESSION_TOKEN_LOCAL_STORAGE_KEY,
  SITE_MAP_DOMAIN_3D,
  SITE_MAP_DOMAIN_GRAPHICS,
  SITE_MAP_DOMAIN_SFX,
  SITE_MAP_DOMAIN_VFX,
} from '@/lib/config'
import { appendAidToRegisterUrl } from '@/lib/attribution/auth'

/**
 * Merges class names using clsx and tailwind-merge.
 * @param {...ClassValue[]} inputs - List of class values to merge.
 * @returns {string} The merged class name string.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Resolves the appropriate cookie domain based on the environment and provided domain.
 * @param {string|false} [domain=false] - The domain to use, or false to auto-resolve.
 * @returns {string} The resolved cookie domain.
 */
export const resolveCookieDomain = (domain: string | false = false): string => {
  if (typeof domain === 'string') {
    return domain
  }

  if (getEnvironment() === 'development') {
    return 'localhost'
  }

  // Try parsing the domain to get the root domain
  const domainParts = window.location.hostname.split('.')
  if (domainParts.length > 2) {
    return `.${domainParts.slice(-2).join('.')}`
  }

  // Fallback to using the hostname with a leading dot
  return `.${window.location.hostname}`
}

/**
 * Retrieves the value of a cookie by name.
 * @param {string} name - The name of the cookie.
 * @returns {string | undefined} The cookie value, or undefined if not found.
 */
export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';')?.shift()
}

/**
 * Deletes a cookie by name, optionally for a specific domain.
 * @param {string} name - The name of the cookie to delete.
 * @param {boolean} [domain=false] - Whether to use a domain (defaults to false).
 */
export const deleteCookie = (name: string, domain: string | false = false) => {
  let cookieDomain = resolveCookieDomain(domain)

  console.debug('Deleting cookie', { name, domain: cookieDomain })

  document.cookie =
    name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/' + (cookieDomain ? '; domain=' + cookieDomain : '')
}

/**
 * Sets a cookie with a name, value, expiration (in days), and optional domain.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to set.
 * @param {number} days - Number of days until expiration.
 * @param {string|false} [domain=false] - Optional domain for the cookie.
 */
export const setCookie = (name: string, value: string, days: number, domain: string | false = false) => {
  let cookieDomain = resolveCookieDomain(domain)

  console.debug('Setting cookie', { name, domain: cookieDomain })

  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  // Seteando Secure y SameSite=None si no estamos en localhost (porque requiere HTTPS)
  let secure = ''
  let sameSite = ''
  if (cookieDomain !== 'localhost') {
    secure = '; Secure'
    sameSite = '; SameSite=None'
  }
  // Setear la cookie con los atributos adicionales
  document.cookie =
    name +
    '=' +
    (value || '') +
    expires +
    '; path=/' +
    (cookieDomain ? '; domain=' + cookieDomain : '') +
    sameSite +
    secure
}

/**
 * Retrieves a persisted value from localStorage or cookies.
 * Cookies have precedence.
 * @param {string} localStorageKey - The key for localStorage.
 * @param {string} cookieKey - The key for the cookie.
 * @returns {string | null} The persisted value, or null if not found.
 */
export const getPersistedValue = (localStorageKey: string, cookieKey: string): string | undefined => {
  let cookieValue = getCookie(cookieKey)
  let localStorageValue = FALLBACK_TO_LOCAL_STORAGE ? localStorage.getItem(localStorageKey) : null

  // Cookies have precedence over localStorage
  return cookieValue ?? localStorageValue ?? undefined
}

/**
 * Sets a value in localStorage and cookies, creating or updating the entry.
 * @param {string} localStorageKey - The key for localStorage.
 * @param {string} cookieKey - The key for the cookie.
 * @param {string} value - The value to set.
 */
export const setPersistedValue = (localStorageKey: string, cookieKey: string, value: string) => {
  if (!value) {
    if (FALLBACK_TO_LOCAL_STORAGE) {
      localStorage.removeItem(localStorageKey)
    }
    deleteCookie(cookieKey)
    return
  }

  console.debug('Persisting value in', { localStorageKey, cookieKey })

  if (FALLBACK_TO_LOCAL_STORAGE) {
    localStorage.setItem(localStorageKey, value)
  }
  setCookie(cookieKey, value, 365)
}

/**
 * Determines the current environment based on the hostname.
 * @returns {string} The environment name ('development', 'qa', or 'production').
 */
export const getEnvironment = (): string =>
  window.location.hostname === 'localhost'
    ? 'development'
    : window.location.hostname === 'user-qa.productioncrate.com' ||
        window.location.hostname === 'staging.productioncrate.com'
      ? 'qa'
      : 'production'

/**
 * Sends form data to a Google Form.
 * @param data - The form data to send.
 * @param data.username - The username.
 * @param data.email - The email address.
 * @param data.plan - The plan type.
 * @param data.score - The score.
 * @param data.feedback - The feedback message.
 */
export const sendGForm = (data: {
  username?: string
  email?: string
  plan?: string
  score?: string
  feedback?: string
}) => {
  console.debug('Sending form data:', data)

  // URL del formulario de Google Forms que termina en /formResponse
  const googleFormURL =
    'https://docs.google.com/forms/d/e/1FAIpQLScSRvd9dL4c8JUTdTzWH_hZUItIFXOKlakDVMY0MV3aq6I3Gg/formResponse'

  // IDs de los campos del formulario. Reemplázalos con los tuyos.
  const entryIDs = {
    username: 'entry.674977585',
    email: 'entry.790349041',
    plan: 'entry.1849155168',
    score: 'entry.1982830785',
    feedback: 'entry.1381715405',
  }

  // Crear objeto FormData y añadir los datos
  const formData = new FormData()
  formData.append(entryIDs.username, data.username ?? '')
  formData.append(entryIDs.email, data.email ?? '')
  formData.append(entryIDs.plan, data.plan ?? '')
  formData.append(entryIDs.score, data.score ?? '')
  formData.append(entryIDs.feedback, data.feedback ?? '')

  // Hacer una solicitud POST al formulario de Google
  return fetch(googleFormURL, {
    method: 'POST',
    body: formData,
    mode: 'no-cors', // Importante para evitar problemas de CORS con Google Forms
  })
}

/**
 * Parse keywords string into an array of keywords.
 * @param keywords
 */
export const parseKeywords = (keywords: string | string[] | undefined) => {
  if (!keywords) return []

  if (typeof keywords === 'string') {
    return keywords
      .replace(/\s/g, ',')
      .split(',')
      .map((keyword) => keyword.trim().toLowerCase())
  }

  return keywords
}

/**
 * Sleep for a given amount of time.
 */
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Get credentials stored locally.
 */
export const getCredentials = () => {
  const accessToken = getPersistedValue(SESSION_TOKEN_LOCAL_STORAGE_KEY, SESSION_TOKEN_COOKIE_KEY)
  const refreshToken = getPersistedValue(REFRESH_TOKEN_LOCAL_STORAGE_KEY, REFRESH_TOKEN_COOKIE_KEY)

  return { accessToken, refreshToken }
}

/**
 * Set credentials in local storage and cookies.
 * @param accessToken
 * @param refreshToken
 */
export const setCredentials = (accessToken: string, refreshToken: string) => {
  console.debug('Setting credentials')

  // Set the access token
  setPersistedValue(SESSION_TOKEN_LOCAL_STORAGE_KEY, SESSION_TOKEN_COOKIE_KEY, accessToken)

  // Set refresh token
  setPersistedValue(REFRESH_TOKEN_LOCAL_STORAGE_KEY, REFRESH_TOKEN_COOKIE_KEY, refreshToken)
}

/**
 * Clear credentials.
 */
export const clearCredentials = () => {
  if (FALLBACK_TO_LOCAL_STORAGE) {
    localStorage.removeItem(SESSION_TOKEN_LOCAL_STORAGE_KEY)
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY)
  }
  deleteCookie(SESSION_TOKEN_COOKIE_KEY)
  deleteCookie(REFRESH_TOKEN_COOKIE_KEY)
}

/**
 * Format a duration in seconds to a string in the format mm:ss.
 * @param duration
 */
export const formatDuration = (duration: number) => {
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}

/**
 * Format a number of bytes into a human-readable string.
 * @param bytes - The number of bytes.
 * @returns A string representing the formatted byte size.
 */
export const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let index = 0
  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024
    index++
  }
  return `${bytes.toFixed(2)} ${units[index]}`
}

/**
 * Converts a domain to a sitemap path.
 * @param domain - The domain to convert.
 * @returns The corresponding sitemap path as a string.
 */
export const domainToSitemapPath = (domain: string): string => {
  let path
  if (SITE_MAP_DOMAIN_VFX && domain.includes(SITE_MAP_DOMAIN_VFX)) {
    path = 'vfx'
  } else if (SITE_MAP_DOMAIN_SFX && domain.includes(SITE_MAP_DOMAIN_SFX)) {
    path = 'sfx'
  } else if (SITE_MAP_DOMAIN_GRAPHICS && domain.includes(SITE_MAP_DOMAIN_GRAPHICS)) {
    path = 'graphics'
  } else if (SITE_MAP_DOMAIN_3D && domain.includes(SITE_MAP_DOMAIN_3D)) {
    path = '3d'
  } else {
    path = 'default'
  }
  return path
}

/**
 * Converts a sitemap path (or what is the same, a main category slug) to a domain.
 * @param path - The sitemap path to convert.
 * @returns The corresponding domain as a string, or null if not found.
 */
export const sitemapPathToDomain = (path: string) => {
  let domain: string | null = null
  if (path === 'vfx') {
    domain = SITE_MAP_DOMAIN_VFX
  } else if (path === 'sfx') {
    domain = SITE_MAP_DOMAIN_SFX
  } else if (path === 'graphics') {
    domain = SITE_MAP_DOMAIN_GRAPHICS
  } else if (path === '3d') {
    domain = SITE_MAP_DOMAIN_3D
  }
  return domain ? `https://${domain}` : null
}

/**
 * Resolve the base URL for a given main category slug.
 * @param mainCategorySlug
 * @returns The resolved base URL as a string.
 */
export const resolveBaseURL = (mainCategorySlug: string) => {
  return sitemapPathToDomain(mainCategorySlug) ?? defaultBaseURL
}

/**
 * Build a search link for a given query and main category slug.
 * @param query - The search query.
 * @param mainCategorySlug - Optional main category slug to filter the search.
 * @returns The constructed search link as a string.
 */
export const getSearchLink = (query: string, mainCategorySlug?: string) => {
  return `/search/${encodeURIComponent(query)}` + (mainCategorySlug ? `?main_category=${mainCategorySlug}` : '')
}

/**
 * Store the intended URL in a cookie for redirecting after login.
 * @param url
 */
export const storeIntendedUrl = (url: string) => {
  try {
    setCookie('crateLoginRedirect', url, 1) // Set cookie for 1 day
    // Verify if the cookie was successfully set
    if (!getCookie('crateLoginRedirect')) {
      console.warn('Failed to set the crateLoginRedirect cookie.')
    }
  } catch (error) {
    console.error('Error setting the crateLoginRedirect cookie:', error)
  }
}

/**
 * Generate register url.
 * This function sets a cookie to redirect the user after registration if an intended URL is provided.
 *
 * @param intendedUrl - The URL to redirect to after registration.
 * @returns The registration URL.
 */
export const generateRegisterUrl = (intendedUrl?: string, msg?: string) => {
  // If the intendedUrl is provided, set a cookie to redirect after registration
  if (intendedUrl) {
    storeIntendedUrl(intendedUrl)
  }
  const registerUrl = `${CRATE_USERS_BASE_URL}/register${msg ? `/${msg}` : ''}`
  return appendAidToRegisterUrl(registerUrl)
}

/**
 * Generate upgrade url.
 * @param intendedUrl
 * @returns The upgrade URL.
 */
export const generateUpgradeUrl = (intendedUrl?: string, msg?: string) => {
  if (intendedUrl) {
    storeIntendedUrl(intendedUrl)
  }
  const usersBaseUrl = getEnvironment() === 'qa' ? 'https://user-qa.productioncrate.com' : CRATE_USERS_BASE_URL
  return `${usersBaseUrl}/upgrade${msg ? `/${msg}` : ''}`
}

/**
 * Generate verify url.
 * @param intendedUrl
 * @returns The verify URL.
 */
export const generateVerifyUrl = (intendedUrl?: string) => {
  if (intendedUrl) {
    storeIntendedUrl(intendedUrl)
  }
  const usersBaseUrl = getEnvironment() === 'qa' ? 'https://user-qa.productioncrate.com' : CRATE_USERS_BASE_URL
  return `${usersBaseUrl}/member/email-verify`
}

/**
 * Down-sample a 1D signal by picking every n-th sample.
 *
 * This performs simple decimation without filtering: for a given `factor`,
 * the function creates a new array whose i-th element is `signal[i * factor]`.
 * The result length is `Math.floor(signal.length / factor)`.
 *
 * Notes and edge cases:
 * - If `factor <= 1`, it is clamped to 1, returning a shallow copy of the
 *   original sequence with the same length.
 * - No anti-aliasing filter is applied. For real audio or dense numeric data,
 *   consider filtering prior to down-sampling to avoid aliasing artifacts.
 * - Accepts any array-like sequence (e.g., number[], Float32Array). Values are
 *   copied into a plain JavaScript array.
 *
 * @param signal - Array-like sequence to down-sample (e.g., number[] or TypedArray).
 * @param factor - Down-sampling factor (integer >= 1). Every `factor`-th item is kept.
 * @returns A new array containing the decimated samples.
 */
export const downSampleSignal = (signal: any, factor: number) => {
  factor = Math.max(factor, 1)
  const newLength = Math.floor(signal.length / factor)
  const newSignal = new Array(newLength)

  for (let i = 0; i < newLength; i++) {
    newSignal[i] = signal[i * factor]
  }

  return newSignal
}

/**
 * Trigger a file download using different strategies.
 *
 * Strategies:
 * - 'navigate': Navigate to the URL, letting the browser handle the download.
 * - 'anchor': Create a temporary anchor element to trigger the download.
 * - 'fetch': Fetch the file as a blob and create an object URL for downloading.
 *
 * @param url
 * @param strategy
 * @param filename
 */
export const triggerDownload = (
  url: string,
  {
    strategy = 'navigate',
    filename = 'file',
  }: {
    strategy?: 'navigate' | 'anchor' | 'fetch'
    filename?: string
  } = {}
) => {
  if (strategy === 'navigate') {
    // Navigate to the URL, letting the browser handle the download
    console.debug('Navigating to download URL:', url)
    window.location.href = url
  } else if (strategy === 'anchor') {
    // Create a temporary anchor element to trigger the download
    console.debug('Creating anchor to download URL:', url)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } else if (strategy === 'fetch') {
    // Fetch the file as a blob and create an object URL for downloading
    console.debug('Fetching file to download from URL:', url)
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = blobUrl
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(blobUrl) // Clean up the object URL
      })
      .catch((error) => {
        console.error('Error fetching the file for download:', error)
      })
  }
}
